import { useState, useRef, useEffect, useMemo, type ReactNode } from 'react';
import { useTheme } from '../state/themeStore';

declare global {
  interface Window {
    monaco?: any;
    require?: any;
  }
}

const MONACO_BASE_URL = 'https://unpkg.com/monaco-editor@0.44.0/min/vs';
const LANGUAGE_ALIASES: Record<string, string> = {
  js: 'javascript',
  jsx: 'javascript',
  ts: 'typescript',
  tsx: 'typescript',
  py: 'python',
  rb: 'ruby',
  sh: 'shell',
  bash: 'shell',
  yml: 'yaml',
  yaml: 'yaml',
  md: 'markdown',
  json: 'json',
  html: 'html',
  css: 'css',
  text: 'plaintext',
};
let monacoLoaderPromise: Promise<void> | null = null;

async function ensureMonaco(): Promise<void> {
  if (typeof window === 'undefined') return;
  if (window.monaco) return;
  if (monacoLoaderPromise) {
    await monacoLoaderPromise;
    return;
  }

  const waitForMonaco = (resolve: () => void, reject: (error: Error) => void) => {
    const attemptLoad = () => {
      if (window.monaco) {
        resolve();
        return;
      }

      const amdRequire = window.require;
      if (amdRequire) {
        amdRequire.config({ paths: { vs: MONACO_BASE_URL } });
        amdRequire(
          ['vs/editor/editor.main'],
          () => resolve(),
          (error: unknown) => {
            monacoLoaderPromise = null;
            reject(error instanceof Error ? error : new Error('Failed to load Monaco Editor'));
          }
        );
        return;
      }

      window.setTimeout(attemptLoad, 50);
    };

    attemptLoad();
  };

  monacoLoaderPromise = new Promise<void>((resolve, reject) => {
    const handleError = () => {
      monacoLoaderPromise = null;
      reject(new Error('Failed to load Monaco Editor'));
    };

    const handleReady = () => {
      waitForMonaco(resolve, reject);
    };

    const existingScript = document.querySelector<HTMLScriptElement>('script[data-monaco-loader="true"]');
    if (existingScript) {
      if (window.monaco) {
        resolve();
        return;
      }

      existingScript.addEventListener('load', handleReady, { once: true });
      existingScript.addEventListener('error', handleError, { once: true });

      if (existingScript.readyState === 'loaded' || existingScript.readyState === 'complete') {
        handleReady();
      }
      return;
    }

    const script = document.createElement('script');
    script.src = `${MONACO_BASE_URL}/loader.js`;
    script.async = true;
    script.dataset.monacoLoader = 'true';
    script.addEventListener('load', handleReady, { once: true });
    script.addEventListener('error', handleError, { once: true });
    document.head.appendChild(script);
  });

  await monacoLoaderPromise;
}

export interface TextContentViewerProps {
  content: string;
  editContent?: string; // Optional separate content for editing
  onSave?: (newContent: string) => void | Promise<void>;
  renderContent?: (content: string) => string;
  readOnly?: boolean;
  language?: string;
  renderLineContent?: (line: string, lineNumber: number) => string | { html: string; className?: string };
  showLineNumbers?: boolean;
  startLineNumber?: number;
  renderCustomContent?: (content: string) => ReactNode;
  initiallyEditing?: boolean;
  enableWordWrap?: boolean;
  focusLocation?: TextFocusLocation | null;
}

export interface TextFocusLocation {
  line: number;
  column?: number;
  length?: number;
}

export function TextContentViewer({
  content,
  editContent: editContentProp,
  onSave,
  renderContent,
  language,
  readOnly = false,
  renderLineContent,
  showLineNumbers = false,
  startLineNumber = 1,
  renderCustomContent,
  initiallyEditing = false,
  enableWordWrap = false,
  focusLocation = null,
}: TextContentViewerProps) {
  const { resolvedThemeId } = useTheme();
  const [isEditing, setIsEditing] = useState(initiallyEditing);
  const [editContent, setEditContent] = useState(editContentProp ?? content);
  const [isSaving, setIsSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const editorInstanceRef = useRef<any>(null);
  const editorDisposablesRef = useRef<Array<{ dispose: () => void }>>([]);
  const isApplyingEditorValueRef = useRef(false);
  const isContentFromEditorRef = useRef(false);
  const [isMonacoReady, setIsMonacoReady] = useState(false);
  const [monacoFailed, setMonacoFailed] = useState(false);
  const handleSaveRef = useRef<() => void>(() => {});
  const handleCancelRef = useRef<() => void>(() => {});
  const { name: monacoThemeName, base: monacoThemeBase } = resolveMonacoTheme(resolvedThemeId);
  const shouldUseMonaco = !renderCustomContent;
  const isEditingRef = useRef(isEditing);
  const fallbackLineRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const normalizedFocusLocation = useMemo(() => {
    if (!focusLocation) {
      return null;
    }
    const line = Math.max(1, focusLocation.line);
    const column = Math.max(1, focusLocation.column ?? 1);
    const length = Math.max(focusLocation.length ?? 1, 1);
    return { line, column, length };
  }, [focusLocation]);

  useEffect(() => {
    isEditingRef.current = isEditing;
  }, [isEditing]);

  useEffect(() => {
    if (!shouldUseMonaco || isMonacoReady || monacoFailed) return;

    let cancelled = false;
    ensureMonaco()
      .then(() => {
        if (!cancelled) {
          setIsMonacoReady(true);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setMonacoFailed(true);
          console.error('[TextContentViewer] Failed to load Monaco:', error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [shouldUseMonaco, isMonacoReady, monacoFailed]);

  useEffect(() => {
    if (!shouldUseMonaco) {
      if (editorInstanceRef.current) {
        editorDisposablesRef.current.forEach((disposable) => disposable.dispose());
        editorDisposablesRef.current = [];
        editorInstanceRef.current.dispose();
        editorInstanceRef.current = null;
      }
      return;
    }

    if (!isMonacoReady || monacoFailed) return;
    if (!editorContainerRef.current || !window.monaco) return;

    const monaco = window.monaco;
    defineDashboardMonacoTheme(monaco, monacoThemeName, monacoThemeBase);

    if (!editorInstanceRef.current) {
      const editor = monaco.editor.create(editorContainerRef.current, {
        value: isEditing ? editContent : content,
        language: renderMonacoLanguage(language),
        theme: monacoThemeName,
        automaticLayout: true,
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
        fontSize: 14,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        lineNumbers: showLineNumbers ? 'on' : 'off',
        renderWhitespace: 'selection',
        readOnly: readOnly || !isEditing,
        wordWrap: enableWordWrap ? 'on' : 'off',
      });

      editorInstanceRef.current = editor;

      const model = editor.getModel();
      if (model) {
        model.updateOptions({ tabSize: 2, insertSpaces: true });
      }

      const changeDisposable = editor.onDidChangeModelContent(() => {
        if (!isEditingRef.current) return;
        if (isApplyingEditorValueRef.current) return;
        isContentFromEditorRef.current = true;
        setEditContent(editor.getValue());
      });

      editorDisposablesRef.current = [changeDisposable];
      editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
        handleSaveRef.current();
      });
    }

    const editor = editorInstanceRef.current;
    if (!editor) return;

    const targetValue = isEditing ? editContent : content;
    if (editor.getValue() !== targetValue) {
      isApplyingEditorValueRef.current = true;
      editor.setValue(targetValue);
      isApplyingEditorValueRef.current = false;
      isContentFromEditorRef.current = false;
    } else {
      isContentFromEditorRef.current = false;
    }

    editor.updateOptions({
      readOnly: readOnly || !isEditing,
      lineNumbers: showLineNumbers ? 'on' : 'off',
      minimap: { enabled: false },
      scrollBeyondLastLine: false,
      wordWrap: enableWordWrap ? 'on' : 'off',
    });

    const model = editor.getModel();
    if (model) {
      monaco.editor.setModelLanguage(model, renderMonacoLanguage(language));
      model.updateOptions({ tabSize: 2, insertSpaces: true });
    }

    monaco.editor.setTheme(monacoThemeName);

    if (isEditing && !readOnly) {
      editor.focus();
    }
  }, [shouldUseMonaco, isMonacoReady, monacoFailed, monacoThemeName, monacoThemeBase, showLineNumbers, isEditing, readOnly, editContent, content, language, enableWordWrap]);

  useEffect(() => {
    if (!isEditing) return;

    if ((!shouldUseMonaco || monacoFailed || !isMonacoReady) && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isEditing, shouldUseMonaco, monacoFailed, isMonacoReady]);

  useEffect(() => {
    if (!normalizedFocusLocation) {
      return;
    }
    if (!shouldUseMonaco || monacoFailed || !isMonacoReady) {
      return;
    }
    const editor = editorInstanceRef.current;
    if (!editor) {
      return;
    }
    const { line, column, length } = normalizedFocusLocation;
    const selection = {
      startLineNumber: line,
      startColumn: column,
      endLineNumber: line,
      endColumn: column + length,
    };
    editor.revealRangeInCenter(selection);
    editor.setSelection(selection);
    editor.focus();
  }, [normalizedFocusLocation, shouldUseMonaco, isMonacoReady, monacoFailed]);

  useEffect(() => {
    if (!normalizedFocusLocation) {
      return;
    }
    if (shouldUseMonaco && isMonacoReady && !monacoFailed) {
      return;
    }
    const lineElement = fallbackLineRefs.current[normalizedFocusLocation.line];
    if (lineElement) {
      lineElement.scrollIntoView({ block: 'center' });
    }
  }, [normalizedFocusLocation, shouldUseMonaco, isMonacoReady, monacoFailed]);

  // Update edit content when content prop changes
  useEffect(() => {
    if (!isEditing) {
      setEditContent(editContentProp ?? content);
    }
  }, [content, editContentProp, isEditing]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
    } catch (error) {
      console.error('[TextContentViewer] Failed to copy:', error);
    }
  };

  const handleEdit = () => {
    if (readOnly) return;
    setEditContent(editContentProp ?? content);
    setIsEditing(true);
  };

  const handleSave = async (stayInEditMode: boolean) => {
    if (!onSave) return;
    setIsSaving(true);
    try {
      await onSave(editContent);
      if (!stayInEditMode) {
        setIsEditing(false);
      }
    } catch (error) {
      console.error('[TextContentViewer] Failed to save:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditContent(editContentProp ?? content);
    setIsEditing(false);
  };

  const handleToolbarSave = () => {
    if (isSaving) return;
    void handleSave(false);
  };

  const handleKeyboardSave = () => {
    if (isSaving) return;
    void handleSave(true);
  };

  handleSaveRef.current = handleKeyboardSave;
  handleCancelRef.current = handleCancel;

  useEffect(() => {
    if (!window.monaco) return;
    if (!shouldUseMonaco) return;
    if (!isMonacoReady) return;
    defineDashboardMonacoTheme(window.monaco, monacoThemeName, monacoThemeBase);
    window.monaco.editor.setTheme(monacoThemeName);
  }, [monacoThemeName, monacoThemeBase, isMonacoReady, shouldUseMonaco]);

  useEffect(() => {
    return () => {
      editorDisposablesRef.current.forEach((disposable) => disposable.dispose());
      editorDisposablesRef.current = [];
      if (editorInstanceRef.current) {
        editorInstanceRef.current.dispose();
        editorInstanceRef.current = null;
      }
    };
  }, []);

  const renderedContent = renderContent ? renderContent(content) : content;
  const shouldRenderMonaco = shouldUseMonaco && isMonacoReady && !monacoFailed;
  const shouldShowTextareaFallback = isEditing && (!shouldUseMonaco || monacoFailed || !isMonacoReady);

  const renderLine = (line: string, lineNumber: number) => {
    if (!renderLineContent) {
      return { html: escapeHtml(line), className: '' };
    }
    const result = renderLineContent(line, lineNumber);
    if (typeof result === 'string') {
      return { html: result, className: '' };
    }
    return { html: result.html, className: result.className ?? '' };
  };

  return (
    <div className={`text-content-viewer ${isEditing ? 'text-content-viewer--editing' : ''}`}>
      <div className="text-content-viewer__toolbar">
        {isEditing ? (
          <>
            <button
              onClick={handleToolbarSave}
              disabled={isSaving}
              className="text-content-viewer__btn"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={handleCancel}
              disabled={isSaving}
              className="text-content-viewer__btn"
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <button
              onClick={handleCopy}
              className="text-content-viewer__btn"
              title="Copy to clipboard"
            >
              Copy
            </button>
            {!readOnly && onSave && (
              <button
                onClick={handleEdit}
                className="text-content-viewer__btn"
                title="Edit content"
              >
                Edit
              </button>
            )}
          </>
        )}
      </div>
      <div className="text-content-viewer__content">
        {shouldRenderMonaco ? (
          <div
            ref={editorContainerRef}
            className="text-content-viewer__editor text-content-viewer__editor--monaco"
          />
        ) : shouldShowTextareaFallback ? (
          <textarea
            ref={textareaRef}
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 's' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                handleKeyboardSave();
              }
            }}
            className="text-content-viewer__editor text-content-viewer__editor--textarea"
            spellCheck={false}
            wrap={enableWordWrap ? 'soft' : 'off'}
          />
        ) : renderCustomContent ? (
          renderCustomContent(content)
        ) : showLineNumbers ? (
          <pre className="text-content-viewer__display text-content-viewer__display--lines">
            <code className="text-content-viewer__code text-content-viewer__code--lines">
              {content.split(/\r?\n/).map((line, index) => {
                const lineNumber = startLineNumber + index;
                const { html, className } = renderLine(line, lineNumber);
                const isFocusedLine = normalizedFocusLocation?.line === lineNumber;
                let lineHtml = html;
                if (isFocusedLine && normalizedFocusLocation && !renderLineContent) {
                  lineHtml = highlightLineHtml(line, normalizedFocusLocation.column, normalizedFocusLocation.length);
                }
                if (!lineHtml) {
                  lineHtml = '&nbsp;';
                }
                const lineClassNames = [
                  'text-content-viewer__line',
                  className,
                  isFocusedLine ? 'text-content-viewer__line--focused' : ''
                ].filter(Boolean).join(' ');
                return (
                  <div
                    key={lineNumber}
                    className={lineClassNames}
                    ref={(element) => {
                      if (element) {
                        fallbackLineRefs.current[lineNumber] = element;
                      } else {
                        delete fallbackLineRefs.current[lineNumber];
                      }
                    }}
                  >
                    <span className="text-content-viewer__line-number">{lineNumber}</span>
                    <span
                      className="text-content-viewer__line-content"
                      dangerouslySetInnerHTML={{ __html: lineHtml === '' ? '&nbsp;' : lineHtml }}
                    />
                  </div>
                );
              })}
            </code>
          </pre>
        ) : (
          <pre className="text-content-viewer__display">
            <code dangerouslySetInnerHTML={{ __html: renderedContent }} />
          </pre>
        )}
      </div>
    </div>
  );
}

function highlightLineHtml(line: string, column: number, length: number): string {
  const normalizedColumn = Number.isFinite(column) ? column : 1;
  const startIndex = Math.max(0, normalizedColumn - 1);
  const matchLength = Math.max(length, 1);
  const endIndex = Math.min(line.length, startIndex + matchLength);
  const before = escapeHtml(line.slice(0, startIndex));
  const match = escapeHtml(line.slice(startIndex, endIndex)) || '&nbsp;';
  const after = escapeHtml(line.slice(endIndex));
  return `${before}<span class="text-content-viewer__match">${match}</span>${after}`;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function renderMonacoLanguage(language?: string): string {
  if (!language) return 'plaintext';
  const normalized = language.toLowerCase();
  return LANGUAGE_ALIASES[normalized] ?? normalized;
}

function resolveMonacoTheme(themeId: string): { name: string; base: 'vs' | 'vs-dark' } {
  const normalized = themeId === 'dawn' ? 'dawn' : (themeId === 'midnight' ? 'midnight' : 'charcoal');
  const base: 'vs' | 'vs-dark' = normalized === 'dawn' ? 'vs' : 'vs-dark';
  return { name: `dashboard-${normalized}`, base };
}

function defineDashboardMonacoTheme(monaco: any, themeName: string, base: 'vs' | 'vs-dark') {
  if (typeof window === 'undefined') return;
  const root = document.documentElement;
  if (!root) return;
  const styles = window.getComputedStyle(root);
  const background = normalizeMonacoColor(getCssVariable(styles, '--surface-2'), base === 'vs' ? '#f3f4f8' : '#1e1e1e');
  const foreground = normalizeMonacoColor(getCssVariable(styles, '--text-primary'), base === 'vs' ? '#1f2937' : '#f4f4f5');
  const secondary = normalizeMonacoColor(getCssVariable(styles, '--text-secondary'), base === 'vs' ? '#4b5563' : '#a1a1aa');
  const border = normalizeMonacoColor(getCssVariable(styles, '--border-subtle'), base === 'vs' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.12)');
  const selection = normalizeMonacoColor(getCssVariable(styles, '--accent-neutral-soft'), base === 'vs' ? 'rgba(59,130,246,0.18)' : 'rgba(148,163,184,0.18)');

  monaco.editor.defineTheme(themeName, {
    base,
    inherit: true,
    rules: [],
    colors: {
      'editor.background': background,
      'editor.foreground': foreground,
      'editorGutter.background': background,
      'editorLineNumber.foreground': secondary,
      'editorLineNumber.activeForeground': foreground,
      'editorCursor.foreground': foreground,
      'editor.lineHighlightBackground': border,
      'editor.selectionBackground': selection,
      'editorIndentGuide.background': border,
      'editorIndentGuide.activeBackground': secondary,
      'editorWhitespace.foreground': secondary,
      'editorLineNumber.dimmedForeground': secondary,
      'minimap.background': background,
    },
  });
}

function getCssVariable(styles: CSSStyleDeclaration, name: string): string {
  const value = styles.getPropertyValue(name);
  const trimmed = value.trim();
  return trimmed;
}

function normalizeMonacoColor(value: string, fallback: string): string {
  const parsed = cssColorToMonaco(value);
  if (parsed) return parsed;
  const fallbackParsed = cssColorToMonaco(fallback);
  return fallbackParsed ?? '#000000';
}

function cssColorToMonaco(color: string): string | null {
  const raw = color.trim();
  if (raw === '') return null;

  if (raw.startsWith('#')) {
    if (raw.length === 4) {
      const r = raw[1];
      const g = raw[2];
      const b = raw[3];
      return `#${r}${r}${g}${g}${b}${b}`;
    }
    if (raw.length === 7 || raw.length === 9) {
      return raw;
    }
    return null;
  }

  const rgbaMatch = raw.match(/^rgba?\(([^)]+)\)$/i);
  if (!rgbaMatch) return null;

  const parts = rgbaMatch[1].split(',').map((part) => part.trim());
  if (parts.length < 3) return null;

  const r = clampColorChannel(parseFloat(parts[0]));
  const g = clampColorChannel(parseFloat(parts[1]));
  const b = clampColorChannel(parseFloat(parts[2]));
  const a = parts.length === 4 ? clampAlpha(parseFloat(parts[3])) : 1;

  const alphaHex = a >= 1 ? '' : toHex(Math.round(a * 255));
  return `#${toHex(r)}${toHex(g)}${toHex(b)}${alphaHex}`;
}

function clampColorChannel(value: number): number {
  if (Number.isNaN(value)) return 0;
  if (value <= 1) {
    return Math.min(255, Math.max(0, Math.round(value * 255)));
  }
  return Math.min(255, Math.max(0, Math.round(value)));
}

function clampAlpha(value: number): number {
  if (Number.isNaN(value)) return 1;
  if (value > 1) return 1;
  if (value < 0) return 0;
  return value;
}

function toHex(value: number): string {
  const hex = value.toString(16).toUpperCase();
  return hex.length === 1 ? `0${hex}` : hex;
}
